import React from 'react';
import { ColumnsType } from 'antd/es/table';
import { useHttpQuery } from '@esg/framework';
import { agencyApis } from '@esg/business-agency';
import { FlexBox, TextCommon, TableCustom } from '@esg/ui';
import { Empty, Spin, Pagination, Tag, Space } from 'antd';

interface CustomerReportContainerProps {
    readonly onSelectCustomer: (customerId: string, branchId: string) => void;
}

interface CustomerRecord {
    id: string;
    customerId: string;
    branchId: string;
    fullName: string;
    email: string;
    phoneNumber: string;
    businessName: string;
    branchName: string;
    country: string;
    state: string;
    city: string;
    address: string;
    postalCode: string;
    timezone: string;
    category: string[];
}

const PAGE_SIZE = 5;
const MAX_BRANCH_SLOTS = 20;
const BRANCH_SLOT_INDEXES = Array.from({ length: MAX_BRANCH_SLOTS }, (_, i) => i);

const CustomerReportContainer: React.FC<CustomerReportContainerProps> = ({ onSelectCustomer }) => {
    const [currentPage, setCurrentPage] = React.useState(1);
    const currentPageSize = PAGE_SIZE;

    const { data: orgsResponse } = useHttpQuery(
        agencyApis.organization.getOrganizations,
        { query: { pageSize: 200 } }
    );

    const { data: branchesResponse, isFetching: isBranchesLoading } = useHttpQuery(
        agencyApis.branch.getBranches,
        { query: { pageSize: 200 } }
    );

    const lastBranchesRef = React.useRef<typeof branchesResponse | undefined>(undefined);
    if (branchesResponse?.items) {
        lastBranchesRef.current = branchesResponse;
    }

    const displayBranchesData = branchesResponse ?? lastBranchesRef.current;
    const safeBranches: any[] = displayBranchesData?.items ?? [];
    const safeOrgs: any[] = orgsResponse?.items ?? [];

    const branchRowSlots = BRANCH_SLOT_INDEXES.map((slotIndex) => safeBranches[slotIndex]);

    const customerQueries = branchRowSlots.map((branch) =>
        useHttpQuery(
            agencyApis.customer.getCustomers,
            { query: { branchId: branch?.id, page: 1, pageSize: 1000 } },
            { enabled: !!branch }
        )
    );

    const isCustomersLoading = customerQueries.some((q, i) => branchRowSlots[i] && q.isFetching);
    const isEverythingLoading = isBranchesLoading || (safeBranches.length > 0 && isCustomersLoading);

    const customerDataSignature = customerQueries
        .map((q) => q.data?.items?.map((item: any) => item.id).join('|') ?? 'x')
        .join(',');

    const allCustomers: CustomerRecord[] = React.useMemo(() => {
        const orgMap = new Map(safeOrgs.map((o: any) => [o.id, o]));
        const merged: CustomerRecord[] = [];

        BRANCH_SLOT_INDEXES.forEach((slotIndex) => {
            const branch = branchRowSlots[slotIndex];
            if (!branch) return;

            const orgInfo: any = orgMap.get(branch.organizationId) || {};
            const items = customerQueries[slotIndex]?.data?.items || [];

            items.forEach((c: any, i: number) => {
                const uniqueId =
                    c.id ||
                    (c.email ? `${branch.id}-${c.email}` : null) ||
                    `${branch.id}-${slotIndex}-${i}`;

                merged.push({
                    id: uniqueId,
                    customerId: c.customerId || c.id,
                    branchId: branch.id,
                    fullName: `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'Unknown',
                    email: c.email || 'N/A',
                    phoneNumber: c.phone || 'N/A',
                    businessName: orgInfo.name || 'N/A',
                    branchName: branch.name || 'N/A',
                    country: branch.country || 'N/A',
                    state: branch.state || 'N/A',
                    city: branch.city || 'N/A',
                    address: branch.address || 'N/A',
                    postalCode: branch.postalCode || 'N/A',
                    timezone: branch.timezone || 'N/A',
                    category: Array.isArray(branch.category) ? branch.category : [],
                });
            });
        });

        return merged;
    }, [branchRowSlots, safeOrgs, customerDataSignature]);

    const totalItems = allCustomers.length;
    const pageStart = (currentPage - 1) * currentPageSize;
    const pagedCustomers = allCustomers.slice(pageStart, pageStart + currentPageSize);

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
        const scrollBody = document.querySelector('.customer-table-wrapper .ant-table-body');
        if (scrollBody) {
            scrollBody.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            const wrapper = document.querySelector('.customer-table-wrapper');
            wrapper?.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const cellStyle = { padding: '2vh', height: '9vh' };

    const cellBorderProps = () => ({
        style: {
            borderRight: '1px solid #f0f0f0',
            whiteSpace: 'normal' as const,
            wordBreak: 'break-word' as const,
            ...cellStyle
        }
    });

    const headerBorderProps = () => ({
        style: { borderRight: '1px solid #f0f0f0' }
    });

    const tableColumns: ColumnsType<CustomerRecord> = [
        {
            title: 'Full Name',
            key: 'fullName',
            width: 160,
            align: 'left',
            onCell: cellBorderProps,
            onHeaderCell: headerBorderProps,
            sorter: (a, b) => a.fullName.localeCompare(b.fullName),
            render: (_, record) => (
                <TextCommon
                    onClick={() => onSelectCustomer(record.customerId, record.branchId)}
                    color="#1890ff"
                    fontWeight={600}
                    cursor="pointer"
                >
                    {record.fullName}
                </TextCommon>
            )
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            width: 200,
            align: 'left',
            onCell: cellBorderProps,
            onHeaderCell: headerBorderProps,
            render: (text: string) => <TextCommon fontSize={13} color="#262626" fontWeight={600}>{text}</TextCommon>
        },
        {
            title: 'Business Name',
            dataIndex: 'businessName',
            key: 'businessName',
            width: 160,
            align: 'left',
            onCell: cellBorderProps,
            onHeaderCell: headerBorderProps,
            sorter: (a, b) => a.businessName.localeCompare(b.businessName),
            render: (text: string) => <TextCommon fontSize={13} color="#262626" fontWeight={500}>{text}</TextCommon>
        },
        {
            title: 'Phone Number',
            dataIndex: 'phoneNumber',
            key: 'phoneNumber',
            width: 140,
            align: 'left',
            onCell: cellBorderProps,
            onHeaderCell: headerBorderProps,
            render: (text: string) => <TextCommon fontSize={13} color="#722ed1" fontWeight={600}>{text}</TextCommon>
        },
        {
            title: 'Branch Name',
            dataIndex: 'branchName',
            key: 'branchName',
            width: 150,
            align: 'left',
            onCell: cellBorderProps,
            onHeaderCell: headerBorderProps,
            sorter: (a, b) => a.branchName.localeCompare(b.branchName),
            render: (text: string) => <TextCommon fontSize={13} color="#595959" fontWeight={500}>{text}</TextCommon>
        },
        {
            title: 'Country',
            dataIndex: 'country',
            key: 'country',
            width: 120,
            align: 'left',
            onCell: cellBorderProps,
            onHeaderCell: headerBorderProps,
            render: (text: string) => <TextCommon fontSize={13} color="#595959" fontWeight={500}>{text}</TextCommon>
        },
        {
            title: 'State',
            dataIndex: 'state',
            key: 'state',
            width: 120,
            align: 'left',
            onCell: cellBorderProps,
            onHeaderCell: headerBorderProps,
            render: (text: string) => <TextCommon fontSize={13} color="#595959" fontWeight={500}>{text}</TextCommon>
        },
        {
            title: 'City',
            dataIndex: 'city',
            key: 'city',
            width: 120,
            align: 'left',
            onCell: cellBorderProps,
            onHeaderCell: headerBorderProps,
            render: (text: string) => <TextCommon fontSize={13} color="#595959" fontWeight={500}>{text}</TextCommon>
        },
        {
            title: 'Time Zone',
            dataIndex: 'timezone',
            key: 'timezone',
            width: 130,
            align: 'left',
            onCell: cellBorderProps,
            onHeaderCell: headerBorderProps,
            render: (text: string) => <TextCommon fontSize={12} color="#8c8c8c">{text}</TextCommon>
        },
        {
            title: 'Postcode',
            dataIndex: 'postalCode',
            key: 'postalCode',
            width: 110,
            align: 'left',
            onCell: cellBorderProps,
            onHeaderCell: headerBorderProps,
            render: (text: string) => <TextCommon fontSize={12} color="#8c8c8c">{text}</TextCommon>
        },
        {
            title: 'Branch Address',
            dataIndex: 'address',
            key: 'address',
            width: 220,
            align: 'left',
            onCell: cellBorderProps,
            onHeaderCell: headerBorderProps,
            render: (text: string) => <TextCommon fontSize={13} color="#595959" fontWeight={500}>{text}</TextCommon>
        },
        {
            title: 'Category',
            key: 'category',
            width: 100,
            align: 'left',
            onCell: cellBorderProps,
            onHeaderCell: headerBorderProps,
            render: (_, record) => (
                <Space size={6} wrap>
                    {record.category.length > 0 ? (
                        record.category.map((cat, idx) => (
                            <Tag color="success" key={idx} style={{ minWidth: 56, textAlign: 'center', margin: 0 }}>
                                {cat}
                            </Tag>
                        ))
                    ) : (
                        <TextCommon fontSize={13} color="#595959" fontWeight={500}>N/A</TextCommon>
                    )}
                </Space>
            )
        }
    ];

    return (
        <FlexBox direction="column" className="report-section" width="100%" gap={16}>
            <TextCommon fontWeight={600} fontSize={14} color="#262626">
                Customer Reports List
            </TextCommon>

            <FlexBox
                className="customer-table-wrapper"
                direction="column"
                width="max(350px, calc(100vw - 285px))"
                backgroundColor="white"
            >
                {isEverythingLoading ? (
                    <FlexBox direction="column" preset="column-center" height={200}>
                        <Spin size="large" />
                    </FlexBox>
                ) : (
                    <TableCustom
                        className="customer-report-table"
                        columns={tableColumns}
                        dataSource={pagedCustomers}
                        pagination={false}
                        bordered
                        sticky
                        tableLayout="fixed"
                        scroll={{ x: 1830 }}
                        showHeader={false}
                        footer={() => (
                            <Pagination
                                className="custom-pagination"
                                current={currentPage}
                                total={totalItems}
                                pageSize={currentPageSize}
                                showSizeChanger={false}
                                onChange={handlePageChange}
                            />
                        )}
                        rowClassName={() => 'customer-table-row'}
                        locale={{
                            emptyText: (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description="No customer info available"
                                />
                            )
                        }}
                    />
                )}
            </FlexBox>
        </FlexBox>
    );
};

export { CustomerReportContainer };